"use client"

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@workspace/web-ui/components/accordion"
import { Avatar, AvatarFallback, AvatarImage } from "@workspace/web-ui/components/avatar"
import { Badge } from "@workspace/web-ui/components/badge"
import { Button } from "@workspace/web-ui/components/button"
import { Card, CardContent } from "@workspace/web-ui/components/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs"
import { Calendar, Check, Circle, CircleChevronDown, Clock, DollarSign, FileText } from "lucide-react"
import { useState } from "react"
import {AlfieChatBot} from "../../components/alfie-chat"
// import { useChatMutation } from "../../api/mutations/chat"

export default function UrgentCarePage() {
  const [showChatBot, setShowChatBot] = useState(false)
  const [activeItem, setActiveItem] = useState("")
  const [activeTodoTask, setActiveTodoTask] = useState("")
  const [activeCompletedTask, setActiveCompletedTask] = useState("")
  // const [initialChatMessage, setInitialChatMessage] = useState("")
  // const [initialThreadId, setInitialThreadId] = useState("")
  // // const [errorMessage, setErrorMessage] = useState("")
  // const { mutate: creatChatMutation } = useChatMutation();

  // Mock user data
  const user = {
    first_name: "Maurice",
    last_name: "Smith",
    avatar: "",
    health_profile: {
      weight: 185,
      target_weight: 165,
      timeframe: "3 months",
    },
  }

  // Mock task data
  const urgentCareTasks = [
    {
      month: "May",
      data: [
        {
          id: "1",
          title: "Get diagnosed with Alfie",
          type: "Consultation" as const,
          completed: false,
          priority: true,
          when: "2025-05-23",
          payment_required: false,
        },
        {
          id: "2",
          title: "Pay for consultation",
          type: "Consultation" as const,
          completed: false,
          priority: true,
          when: "2025-05-24",
          payment_required: true,
          consultation_id: "cons-123",
        },
        {
          id: "3",
          title: "Join appointment",
          type: "Appointment" as const,
          completed: false,
          priority: true,
          when: "2025-05-25",
          payment_required: false,
        },
      ],
    },
  ]

  const completedStages = urgentCareTasks.map((monthData) => monthData.data.every((task) => task.completed))

  interface Task {
    id: string
    title: string
    type: "Consultation" | "Appointment"
    completed: boolean
    priority: boolean
    when: string
    payment_required: boolean
    consultation_id?: string
  }

  const handleChatBotOpen = () => {
    setShowChatBot(true)
  }

  const renderTaskActionButton = (task: Task): React.ReactNode => {
    // Talk with Alfie
    if (task.type === "Consultation" && !task.payment_required && !task.completed) {
      return (
        <Button onClick={handleChatBotOpen} className="w-full sm:w-auto rounded-lg bg-[#4272DD]">
          Talk to Alfie
        </Button>
      )
    }

    // Pay for consultation
    if (task.type === "Consultation" && task.payment_required) {
      return <Button className="w-full sm:w-auto rounded-lg bg-[#4272DD]">Make payment</Button>
    }

    // Join appointment
    if (task.type === "Appointment" && !task.payment_required && !task.completed) {
      return <Button className="w-full sm:w-auto rounded-lg bg-[#4272DD]">Join appointment</Button>
    }

    return null
  }

  // interface AccordionContentDetails {
  //   text: string;
  //   value: string;
  // }

  // interface TaskDetails {
  //   date?: string;
  //   amount?: string;
  //   consultationType?: string;
  // }

  const renderAccordionContent = (task: Task): React.ReactNode => {
    if (task.completed) {
      // Content for completed tasks
      return (
        <div className="space-y-4 w-full">
          <div className="space-y-2">
            <h3 className="text-base font-medium">What to expect next</h3>
            <ol className="list-decimal pl-5 space-y-1">
              {task.type === "Appointment" && (
                <>
                  <li className="text-sm text-gray-700">
                    Review and implement the discussed recommendations or action items.
                  </li>
                  <li className="text-sm text-gray-700">
                    You will receive notifications and updates on your dashboard.
                  </li>
                  <li className="text-sm text-gray-700">
                    You can contact support if you have any questions or concerns.
                  </li>
                </>
              )}
              {task.type === "Consultation" && (
                <>
                  <li className="text-sm text-gray-700">
                    Our medical team will review your case and create a personalized plan.
                  </li>
                  <li className="text-sm text-gray-700">
                    You will receive notifications and updates on your dashboard.
                  </li>
                  <li className="text-sm text-gray-700">
                    You can contact support if you have any questions or concerns.
                  </li>
                </>
              )}
            </ol>
          </div>

          {/* Display additional information for completed tasks */}
          {(task.type === "Consultation" || task.type === "Appointment") && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-start gap-2">
                <Calendar className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Date initiated</p>
                  <p className="text-sm font-medium">{task.when || "24 April, 2025"}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <DollarSign className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Amount Paid:</p>
                  <p className="text-sm font-medium">{"$50.00"}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <FileText className="size-5 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">
                    {task.type === "Consultation" || task.type === "Appointment"
                      ? "Consultation Details:"
                      : "Medications"}
                  </p>
                  <p className="text-sm font-medium">
                    {task.type === "Consultation" || task.type === "Appointment"
                      ? "Urgent Care Consultation"
                      : "Prescribed Medications"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    } else {
      // Content for pending tasks
      return (
        <div className="space-y-4 w-full">
          <div className="space-y-2">
            <h3 className="text-base font-medium">What to do</h3>
            <ol className="list-decimal pl-5 space-y-1">
              {task.type === "Consultation" && !task.payment_required && (
                <>
                  <li className="text-sm text-gray-700">
                    Review any provided materials and come prepared with questions or topics to discuss
                  </li>
                  <li className="text-sm text-gray-700">Provide detailed information to get an accurate assessment</li>
                  <li className="text-sm text-gray-700">Make payment for the consultation</li>
                </>
              )}
              {task.type === "Consultation" && task.payment_required && (
                <>
                  <li className="text-sm text-gray-700">Pay now to start your consultation.</li>
                  <li className="text-sm text-gray-700">
                    Our team will review your case and create a personalized plan.
                  </li>
                  <li className="text-sm text-gray-700">You'll receive updates on your dashboard.</li>
                </>
              )}
              {task.type === "Appointment" && (
                <>
                  <li className="text-sm text-gray-700">
                    Review any provided materials and come prepared with questions or topics to discuss
                  </li>
                  <li className="text-sm text-gray-700">
                    Click the meeting link or dial-in number to join at the scheduled time
                  </li>
                </>
              )}
            </ol>
          </div>

          <div className="pt-2">
            <p className="text-sm text-gray-700">
              Contact Support: Need help? Reach out to our support team appointments@proxymedicine.com
            </p>
          </div>
        </div>
      )
    }
  }

  return (
    <main className="flex flex-col gap-6">
      <section className="py-10">
        {/* Greetings */}
        <span className="inline-flex gap-2 pb-4">
          <h6 className="text-lg font-normal">Welcome,</h6>
          <h6 className="text-lg font-bold">{user?.first_name}</h6>
        </span>

        {/* Subnav one */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-bold mb-4 sm:mb-0">Urgent Care</h1>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button variant="outline" className="border-[#4272DD] text-[#4272DD]" onClick={handleChatBotOpen}>
              Start a treatment
            </Button>
          </div>
        </div>

        {/* Accordion for user details */}
        <Accordion type="single" collapsible value={activeItem} onValueChange={setActiveItem} className="w-full">
          <AccordionItem
            value="user-details"
            className="mb-5 w-full rounded-2xl bg-white px-3 text-black shadow-gray-500"
          >
            <AccordionTrigger className="flex w-full justify-start gap-3 py-3 hover:no-underline sm:flex-row sm:justify-between [&>svg:last-child]:hidden">
              <div className="flex flex-col space-y-2 sm:space-y-0 flex-1 sm:flex-row sm:items-center sm:justify-start">
                {/* Top Row: Avatar + Username */}
                <div className="flex items-center justify-start gap-3">
                  <Avatar className="size-8">
                    <AvatarImage src={user?.avatar || ""} />
                    <AvatarFallback>{`${user?.first_name?.charAt(0)}${user?.last_name?.charAt(0)}`}</AvatarFallback>
                  </Avatar>
                  <h6 className="text-base font-bold sm:text-lg">
                    {user?.first_name} {user?.last_name}
                  </h6>
                </div>

                {/* Stats go under username on mobile, inline on desktop */}
                <div className="flex flex-wrap justify-start sm:items-center sm:gap-y-0 gap-x-4 sm:pl-4">
                  <span className="inline-flex gap-1 text-xs sm:text-sm">
                    <h6 className="font-normal">Current Weight:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">{user?.health_profile?.weight} lbs</h6>
                  </span>
                  <span className="inline-flex gap-1 text-xs sm:text-sm ">
                    <h6 className="font-normal">Goal Weight:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">
                      {user?.health_profile?.target_weight} lbs
                    </h6>
                  </span>
                  <span className="inline-flex gap-1 text-xs my-3 sm:text-sm ">
                    <h6 className="font-normal">Time Frame:</h6>
                    <h6 className="font-normal sm:font-bold sm:text-sm text-xs">{user?.health_profile?.timeframe}</h6>
                  </span>
                </div>
              </div>

              <CircleChevronDown
                strokeWidth={0.75}
                className="size-8 text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
              />
            </AccordionTrigger>

            <AccordionContent className="w-full px-4 py-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">Medical Conditions</h3>
                    <p className="text-sm text-gray-600">Hypertension, Asthma</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">Allergies</h3>
                    <p className="text-sm text-gray-600">Penicillin, Peanuts</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">Current Medications</h3>
                    <p className="text-sm text-gray-600">Lisinopril, Albuterol</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">Recent Vitals</h3>
                    <p className="text-sm text-gray-600">BP: 120/80, HR: 72, Temp: 98.6°F</p>
                  </CardContent>
                </Card>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Timeline */}
        <div className="space-y-8">
          {urgentCareTasks.map((monthData, monthIndex) => (
            <div key={monthData.month} className="flex flex-col sm:flex-row">
              {/* Timeline Indicator - Hidden on mobile */}
              <div className="relative hidden w-10 flex-col items-center sm:flex">
                {completedStages[monthIndex] ? (
                  <Circle className="h-6 w-6 text-green-500" />
                ) : (
                  <>
                    <div className="relative flex size-10 items-center justify-center">
                      <Circle
                        className={`absolute size-7 ${
                          monthIndex === 0 || completedStages[monthIndex - 1] ? "text-[#4272DD]" : "text-[#DCDCDE]"
                        }`}
                      />
                      <Circle
                        strokeWidth={5}
                        className={`absolute size-3 ${
                          monthIndex === 0 || completedStages[monthIndex - 1] ? "text-[#4272DD]" : "text-[#DCDCDE]"
                        }`}
                      />
                    </div>

                    <div
                      className={`-mt-2 h-full w-1 ${monthIndex === 0 ? "bg-gradient-to-b from-blue-500 to-transparent" : "bg-gradient-to-b from-gray-300 to-transparent opacity-40"}`}
                    ></div>
                  </>
                )}
                {monthIndex < urgentCareTasks.length - 1 && (
                  <div
                    className={`-mt-2 h-full w-1 ${
                      monthIndex === 0
                        ? "bg-gradient-to-b from-[#4272DD] to-transparent"
                        : "bg-gradient-to-b from-[#DCDCDE] to-transparent opacity-40"
                    }`}
                  ></div>
                )}
              </div>

              {/* Card Section */}
              <Card className="w-full">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between gap-2 border-b px-6 pb-4 pt-3 sm:flex-row sm:items-center">
                    <h2 className="text-lg font-semibold">#1 Your Care Timeline</h2>
                    <Badge
                      className={`rounded-2xl text-sm hover:bg-[#E1FAEA] ${
                        completedStages[monthIndex] ? "bg-[#E1FAEA] text-[#14AD4D]" : "bg-[#FFF1DA] text-[#9D6508]"
                      }`}
                    >
                      {completedStages[monthIndex] ? "Completed" : "Ongoing"}
                    </Badge>
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue="to-do">
                    <TabsList className="flex h-12 w-full flex-wrap justify-start gap-4 rounded-none border-b bg-background px-6 py-0">
                      <TabsTrigger
                        value="to-do"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 py-4 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        To-do
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task) => !task.completed).length}
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger
                        value="completed"
                        className="h-full rounded-none border-b-2 border-transparent bg-background px-0 text-sm data-[state=active]:border-primary data-[state=active]:shadow-none"
                      >
                        Completed
                        <Badge className="ml-2 size-4 items-center justify-center rounded-full bg-[#FFAB1F] p-1 text-center text-xs">
                          {monthData.data.filter((task) => task.completed).length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    {/* To-do Tasks */}
                    <TabsContent value="to-do" className="px-6">
                      {monthData.data.length > 0 ? (
                        <>
                          {monthData.data.filter((task) => !task.completed).length > 0 ? (
                            <Accordion
                              type="single"
                              collapsible
                              value={activeTodoTask}
                              onValueChange={setActiveTodoTask}
                              className="w-full"
                            >
                              {monthData.data
                                .filter((task) => !task.completed)
                                .map((task, index) => (
                                  <AccordionItem
                                    key={`todo-${monthData.month}-${index}`}
                                    value={`todo-${monthData.month}-${index}`}
                                    className="my-4 border rounded-[20px] shadow-sm"
                                  >
                                    <AccordionTrigger className="px-4 py-3 relative hover:no-underline [&>svg:last-child]:hidden">
                                      <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                                        <div className="flex items-start gap-3 flex-1">
                                          <Circle
                                            strokeWidth={2}
                                            className="size-6 shrink-0 text-[#4272DD] mt-1 sm:mt-0"
                                          />
                                          <div className="flex text-left">
                                            <h3 className="text-base font-medium break-words">{task.title}</h3>
                                            {task.type === "Appointment" && task.when ? (
                                              <span className="text-sm text-[#2D2F35] flex items-center gap-2 flex-wrap mt-1">
                                                <Clock size={16} className="text-[#7F8493]" />
                                                <h6 className="text-xs text-[#202020CC]">{task.when}</h6>
                                              </span>
                                            ) : null}
                                          </div>
                                          {task.priority && (
                                            <Badge className="h-6 w-16 hidden sm:flex rounded-3xl bg-[#FFF1DA] text-[#9D6508] hover:bg-[#FFF1DA]">
                                              Priority
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                                          {renderTaskActionButton(task)}
                                        </div>
                                      </div>
                                      <CircleChevronDown
                                        strokeWidth={0.75}
                                        className="size-8 top-0 mt-3 md:mt-0 md:ml-2 right-0 mr-2 md:mr-0 text-[#7F8493] absolute md:relative transition-transform duration-300 data-[state=open]:rotate-180"
                                      />
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 py-2">
                                      <div className="flex mt-2">{renderAccordionContent(task)}</div>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                            </Accordion>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8">
                              <div className="rounded-full bg-gray-100 p-3 mb-4">
                                <Check className="h-6 w-6 text-green-500" />
                              </div>
                              <h3 className="text-xl font-medium mb-2">All caught up!</h3>
                              <p className="text-gray-500 text-center max-w-md">
                                You've completed all your urgent care tasks. Check the completed tab to see your
                                progress.
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="rounded-full bg-gray-100 p-3 mb-4">
                            <FileText className="h-6 w-6 text-gray-400" />
                          </div>
                          <h3 className="text-xl font-medium mb-2">No tasks yet</h3>
                          <p className="text-gray-500 text-center max-w-md">
                            You don't have any urgent care tasks yet. Start by talking to Alfie or booking an
                            appointment.
                          </p>
                        </div>
                      )}
                    </TabsContent>

                    {/* Completed Tasks */}
                    <TabsContent value="completed" className="px-6">
                      {monthData.data.filter((task) => task.completed).length > 0 ? (
                        <Accordion
                          type="single"
                          collapsible
                          value={activeCompletedTask}
                          onValueChange={setActiveCompletedTask}
                          className="w-full"
                        >
                          {monthData.data
                            .filter((task) => task.completed)
                            .map((task, index) => (
                              <AccordionItem
                                key={`completed-${monthData.month}-${index}`}
                                value={`completed-${monthData.month}-${index}`}
                                className="my-4 border rounded-[20px] shadow-sm bg-[#EBFBF1]"
                              >
                                <AccordionTrigger className="px-4 py-3 hover:no-underline [&>svg:last-child]:hidden">
                                  <div className="flex w-full justify-between items-center">
                                    <div className="flex items-start gap-3">
                                      <div className="flex size-6 items-center justify-center rounded-full bg-[#14AD4D]">
                                        <Check className="size-3 text-white" strokeWidth={3} />
                                      </div>
                                      <div className="flex flex-col text-left">
                                        <h3 className="text-base font-medium">{task.title}</h3>
                                        <p className="text-sm text-gray-500">Completed</p>
                                      </div>
                                    </div>
                                  </div>
                                  <CircleChevronDown
                                    strokeWidth={0.75}
                                    className="size-8 text-[#7F8493] transition-transform duration-300 data-[state=open]:rotate-180"
                                  />
                                </AccordionTrigger>
                                <AccordionContent className="px-4 py-2">
                                  <div className="flex mt-2">{renderAccordionContent(task)}</div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                        </Accordion>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="rounded-full bg-gray-100 p-3 mb-4">
                            <FileText className="h-6 w-6 text-gray-400" />
                          </div>
                          <h3 className="text-xl font-medium mb-2">No completed tasks</h3>
                          <p className="text-gray-500 text-center max-w-md">
                            You haven't completed any urgent care tasks yet. Check the to-do tab to see what needs to be
                            done.
                          </p>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Chatbot using Sheet component */}
      <AlfieChatBot open={showChatBot} onOpenChange={setShowChatBot} />
    </main>
  )
}
